import React, { useCallback, useEffect, useRef, useState } from 'react';
import AddToPlaylistPopup from './AddToPlaylistPopup';
import { itunesSearchUrl, lookupTrackUrl, storeTrackUrl } from './itunes';
import './MatchPanel.css';

// DJB-266/279: the status box on the right of an open song request card, a
// white pill. GET /webforms/songs gives `match: { trackId, title, artist } |
// null` (DJB-262, no score any more): a match reads ♪ "Match found!" with
// the matched "Songtitle - Artistname" and "Add to Playlist ›" (the
// playlists popover, DJB-280: a fixed portal above the nav); without one it
// reads ✕ "No match found…" with "Show in iTunes ›" (DJB-281: a link to the
// track itself, see itunes.js). A completed card shows the Completed box
// instead (RequestDayList).

export const hasLibraryMatch = (match) => match != null;

export { ITUNES_SEARCH_URL, itunesSearchUrl, itunesUrl } from './itunes';

// DJB-281: the href of "Show in iTunes" and where it came from —
// 'store' (storeUrl of the request, a track URL), 'lookup' (the
// trackViewUrl found in the iTunes Search API) or 'search' (the Apple
// Music search page, while the lookup runs and when it finds nothing)
export const useItunesUrl = (item) => {
  const store = storeTrackUrl(item);
  const { titel, artiest } = item;
  const [found, setFound] = useState(null);

  useEffect(() => {
    if (store) return undefined;
    let active = true;
    setFound(null);
    lookupTrackUrl(titel, artiest)
      .then((url) => {
        if (active) setFound(url || null);
      })
      .catch((e) => {
        console.error('iTunes lookup failed:', e);
      });
    return () => {
      active = false;
    };
  }, [store, titel, artiest]);

  if (store) return { href: store, source: 'store' };
  if (found) return { href: found, source: 'lookup' };
  return { href: itunesSearchUrl(titel, artiest), source: 'search' };
};

const NoteIcon = () => (
  <svg className="match-icon match-icon-note" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path
      d="M8 4v9.2a2.6 2.6 0 1 0 1.6 2.4V7.3l5-1.4V11a2.6 2.6 0 1 0 1.6 2.4V2.8L8 4.6z"
      fill="currentColor"
    />
  </svg>
);

const CrossIcon = () => (
  <svg className="match-icon match-icon-cross" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <circle cx="10" cy="10" r="9" fill="currentColor" />
    <path d="M7 7l6 6M13 7l-6 6" stroke="var(--theme-surface, #fff)" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const NoMatchPanel = ({ item }) => {
  const { href, source } = useItunesUrl(item);
  return (
    <div className="match-panel-wrap">
      <div className="match-panel request-status match-none" data-testid="request-status">
        <CrossIcon />
        <span className="match-text">
          <span className="match-heading">No match found…</span>
        </span>
        <a
          className="match-action"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-itunes-source={source}
        >
          Show in iTunes <span aria-hidden="true">›</span>
        </a>
      </div>
    </div>
  );
};

const MatchPanel = ({ item }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const anchorRef = useRef(null);
  const match = item.match;

  if (hasLibraryMatch(match)) {
    return (
      <div className="match-panel-wrap" ref={anchorRef}>
        <div className="match-panel request-status match-found" data-testid="request-status">
          <NoteIcon />
          <span className="match-text">
            <span className="match-heading">Match found!</span>
            <span className="match-track">{`${match.title} - ${match.artist}`}</span>
          </span>
          <button
            type="button"
            className="match-action"
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            Add to Playlist <span aria-hidden="true">›</span>
          </button>
        </div>
        {open && <AddToPlaylistPopup trackId={match.trackId} onClose={close} anchorRef={anchorRef} />}
      </div>
    );
  }

  return <NoMatchPanel item={item} />;
};

export default MatchPanel;
